// Implements: F-ITK-007
/**
 * The pure rules of the WhatsApp linking section: which scopes a number may be linked to, how an
 * instant reads, and where a linked number creates.
 *
 * **The offered scopes are the person's own personal scope, then their organization and team
 * seats** (owner decision 2026-10-26: a personal mockup generates within the personal monthly
 * quota, so the creation form offers it and so does linking). The personal scope travels with no
 * `scope_id`: the server completes it with the person's id. The first seat is chosen until the
 * person picks another, or the personal scope when there is no seat. The server still decides
 * (`403` when the person may not create in the scope).
 */

import type { ScopeEntry } from "../../../types/api";
import type { IssueLinkCodeInput, SenderBindingView } from "../../../types/intake";
import { PLATFORM_HOUR_CYCLE, PLATFORM_LOCALE } from "../../../types/locale";

export interface LinkScopeOption {
  /** `kind:id`: two seats of different kinds never collide. */
  readonly value: string;
  readonly label: string;
  /** The exact body of `POST /intake/link-codes` for this seat. */
  readonly input: IssueLinkCodeInput;
}

export function linkScopeOptions(seats: readonly ScopeEntry[], personalLabel: string): readonly LinkScopeOption[] {
  const options: LinkScopeOption[] = [{ value: "personal:", label: personalLabel, input: { scope_kind: "personal" } }];
  for (const seat of seats) {
    if (seat.kind === "platform" || seat.id === null) continue;
    options.push({
      value: `${seat.kind}:${seat.id}`,
      label: seat.name ?? seat.id,
      input: { scope_kind: seat.kind, scope_id: seat.id },
    });
  }
  return options;
}

/** The scope chosen before the person picks one: the first seat, or the personal scope without seats. */
export function defaultLinkScope(options: readonly LinkScopeOption[]): LinkScopeOption | undefined {
  return options.find((option) => option.input.scope_kind !== "personal") ?? options[0];
}

/** A date and a 24-hour time in the platform locale (`types/locale.ts`) and the reader's own zone. */
const MOMENT = new Intl.DateTimeFormat(PLATFORM_LOCALE, {
  dateStyle: "medium",
  timeStyle: "short",
  hourCycle: PLATFORM_HOUR_CYCLE,
});

/** The instant as text, never as an exception: a value `Date` cannot read is handed back as it came. */
export function momentText(raw: string): string {
  const instant = Date.parse(raw);
  return Number.isNaN(instant) ? raw : MOMENT.format(new Date(instant));
}

/** The name of the seat a binding creates in, or `null` when the person no longer holds it. */
export function bindingScopeName(binding: SenderBindingView, seats: readonly ScopeEntry[]): string | null {
  const seat = seats.find((each) => each.kind === binding.scope_kind && each.id === binding.scope_id);
  return seat === undefined ? null : (seat.name ?? seat.id);
}
